import { useParams, Navigate, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import {
  useCard,
  useFavorites,
  useAddToFavorites,
  useRemoveFromFavorites,
} from '../../hooks/useApi'
import { useAppStore } from '../../store/useAppStore'
import { useTelegramBackButton } from '../../hooks/useTelegramBackButton'
import { ContentRenderer, ImageSlider } from './components'
import favorite from '../../assets/images/yesFavorite.png'
import noFavorite from '../../assets/images/noFavorite.png'
import './CardPage.css'
import { File, Timer, Share2, Calculator } from 'lucide-react'

import { CALCULATOR_RECIPE_IDS } from '../../constants/calculatorAccess'
import LoadingScreen from '../../components/LoadingScreen/LoadingScreen'
import SubscriptionBanner from '../../components/SubscriptionBanner'
import SubscriptionBlock from '../../components/SubscriptionBlock'

const CardPage = () => {
  const { cardId } = useParams<{ cardId: string }>()
  const cardIdNumber = cardId ? parseInt(cardId, 10) : null
  const navigate = useNavigate()

  // Состояние для минимального времени загрузки
  const [isMinLoadingComplete, setIsMinLoadingComplete] = useState(false)

  // Все хуки должны быть вызваны до любых условных возвратов
  const { data: cardData, isLoading, error } = useCard(cardIdNumber)
  const { favorites, hasActiveSubscription } = useAppStore()

  // Показываем кнопку "Назад" в Telegram WebApp
  useTelegramBackButton(true)

  // Минимальное время загрузки 3 секунды
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsMinLoadingComplete(true)
    }, 2000) // 3 секунды

    return () => clearTimeout(timer)
  }, [])

  // Хуки для работы с избранным
  useFavorites() // Загружаем избранное при монтировании
  const addToFavoritesMutation = useAddToFavorites()
  const removeFromFavoritesMutation = useRemoveFromFavorites()

  // Проверяем, находится ли карточка в избранном
  const isFavorite = cardId ? favorites.includes(cardId) : false

  // Функция для переключения избранного
  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!cardIdNumber) return

    try {
      if (isFavorite) {
        await removeFromFavoritesMutation.mutateAsync({ card_id: cardIdNumber })
      } else {
        await addToFavoritesMutation.mutateAsync({ card_id: cardIdNumber })
      }
    } catch (error) {
      console.error('Ошибка при изменении избранного:', error)
    }
  }

  // Функция для копирования ссылки на карточку
  const handleShare = async () => {
    if (!cardId) return

    // Формируем ссылку для Telegram бота (startapp открывает веб-приложение)
    const shareUrl = `https://telegram.me/pauline_secret_bot/app?startapp=card_${cardId}`

    try {
      // Копируем ссылку в буфер обмена
      await navigator.clipboard.writeText(shareUrl)

      // Тактильная обратная связь
      const tg = window.Telegram?.WebApp
      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('success')
      }

      // Показываем уведомление (можно добавить toast, но пока используем alert)
      // В будущем можно заменить на более красивое уведомление
      alert('Ссылка скопирована в буфер обмена!')
    } catch (error) {
      console.error('Ошибка при копировании ссылки:', error)
      alert('Не удалось скопировать ссылку')
    }
  }

  // Если cardId не валидный, перенаправляем на главную
  if (!cardId || isNaN(cardIdNumber!)) {
    return <Navigate to="/" replace />
  }

  // Показываем загрузку, если данные еще загружаются ИЛИ минимальное время еще не прошло
  if (isLoading || !isMinLoadingComplete) {
    return <LoadingScreen isLoading={true} />
  }

  if (error) {
    // Проверяем, является ли ошибка требованием подписки
    const errorDetail = (error as any)?.response?.data?.detail
    const isSubscriptionRequired = errorDetail === 'Для доступа к этой карточке требуется активная подписка'

    if (isSubscriptionRequired) {
      return (
        <div className="card-page-error">
          <h2>Требуется подписка</h2>
          <p>Для доступа к этой карточке необходима активная подписка</p>
          <button onClick={() => window.location.href = '/subscribe'}>
            Оформить подписку
          </button>
          <button 
            className="secondary"
            onClick={() => window.history.back()}
          >
            Вернуться назад
          </button>
        </div>
      )
    }

    return (
      <div className="card-page-error">
        <h2>Ошибка загрузки</h2>
        <p>Не удалось загрузить данные карточки</p>
        <button onClick={() => window.history.back()}>Вернуться назад</button>
      </div>
    )
  }

  if (!cardData?.card) {
    return (
      <div className="card-page-error">
        <h2>Карточка не найдена</h2>
        <p>Запрашиваемая карточка не существует</p>
        <button onClick={() => window.history.back()}>Вернуться назад</button>
      </div>
    )
  }

  const { card, info } = cardData

  return (
    <div
      className={`card-page ${
        card.slider_images &&
        card.slider_images.length === 0 &&
        'safe-area-inset'
      }`}
    >
      {card.slider_images && card.slider_images.length > 0 && (
        <ImageSlider images={card.slider_images} title={card.title} />
      )}
      <div className="card-page-content">
        <div className="card-info">
          {/* Заголовок и кнопка поделиться */}
          <div className="card-header-row">
            <h1 className="card-title">{info.title}</h1>
            <button
              className="share-btn"
              onClick={handleShare}
              title="Поделиться"
            >
              <Share2 size={20} />
            </button>
          </div>
          {/* Показываем время приготовления и кнопку скачивания только для рецептов */}
          {card.card_type === 'recipe' && (
            <div className="recipe-meta">
              {card.cooking_time && (
                <div className="cooking-time">
                  <Timer size={24} className="timer-icon" />
                  <p>{card.cooking_time}</p>
                </div>
              )}

              <div className="recipe-actions">
                {card.file_url && (
                  <button
                    onClick={() =>
                      window.open(
                        card.file_url,
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                    className="download-btn"
                  >
                    <File size={16} />
                    <p>Скачать рецепт</p>
                  </button>
                )}

                {/* Кнопка избранного - показываем только для пользователей с подпиской */}
                {hasActiveSubscription() && (
                  <button
                    className={`favorite-btn ${isFavorite ? 'favorite-active' : ''}`}
                    onClick={handleToggleFavorite}
                    disabled={
                      addToFavoritesMutation.isLoading ||
                      removeFromFavoritesMutation.isLoading
                    }
                    title={
                      isFavorite
                        ? 'Удалить из избранного'
                        : 'Добавить в избранное'
                    }
                  >
                    <img
                      src={isFavorite ? favorite : noFavorite}
                      alt={isFavorite ? 'В избранном' : 'Добавить в избранное'}
                      className="favorite-icon"
                    />
                    <p>{isFavorite ? 'В избранном' : 'В избранное'}</p>
                  </button>
                )}
              </div>

              {CALCULATOR_RECIPE_IDS.has(Number(cardId)) && (
                <button
                  className="calculator-btn"
                  onClick={() => navigate(`/calculator/${cardId}`)}
                >
                  <Calculator size={16} />
                  <p>Пересчитать</p>
                </button>
              )}
            </div>
          )}

          {/* Блок с рекламой подписки - показываем только для пользователей без подписки */}

          {card.card_type !== 'recipe' && hasActiveSubscription() && (
            <div className="card-actions">
              <button
                className={`favorite-btn ${isFavorite ? 'favorite-active' : ''}`}
                onClick={handleToggleFavorite}
                disabled={
                  addToFavoritesMutation.isLoading ||
                  removeFromFavoritesMutation.isLoading
                }
                title={
                  isFavorite ? 'Удалить из избранного' : 'Добавить в избранное'
                }
              >
                <img
                  src={isFavorite ? favorite : noFavorite}
                  alt={isFavorite ? 'В избранном' : 'Добавить в избранное'}
                  className="favorite-icon"
                />
                {isFavorite ? 'В избранном' : 'В избранное'}
              </button>
            </div>
          )}
        </div>
        {!hasActiveSubscription() && (
          <SubscriptionBanner hasSubscription={hasActiveSubscription()} />
        )}
        {/* Рендерим контент карточки */}
        {card.content && card.content.length > 0 && (
          <ContentRenderer content={card.content} />
        )}
        {!hasActiveSubscription() && (
          <SubscriptionBlock
            hasSubscription={hasActiveSubscription()}
            buttonText="Узнать подробнее"
          />
        )}
      </div>
    </div>
  )
}

export default CardPage
